import React from 'react';

/**
 * MultiChoiceQuestion Component
 * Renders a multiple choice question with radio button options
 */
export const MultiChoiceQuestion = ({ 
  question, 
  answer, 
  onAnswerChange,
  readonly = false 
}) => {
  const options = question.options || [];
  
  return (
    <div className="question-container" style={{ marginBottom: '20px' }}>
      <div className="question-header" style={{ marginBottom: '12px' }}>
        <h4 style={{ margin: '0 0 4px 0', fontSize: '15px', fontWeight: '600', color: '#1f2937' }}>
          {question.question}
        </h4>
        {question.points && (
          <span style={{ fontSize: '12px', color: '#6b7280', fontWeight: '500' }}>
            {question.points} point{question.points !== 1 ? 's' : ''}
          </span>
        )}
      </div>
      
      <div className="options-container" style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {options.map((option, idx) => (
          <label key={idx} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
            <input
              type="radio"
              name={`question_${question.id}`}
              value={option}
              checked={answer === option}
              onChange={(e) => onAnswerChange(question.id, e.target.value)}
              disabled={readonly}
              style={{ cursor: 'pointer' }}
            />
            <span style={{ fontSize: '14px', color: '#374151', flex: 1 }}>
              {option}
            </span>
          </label>
        ))}
      </div>
    </div>
  );
};

/**
 * ShortAnswerQuestion Component
 * Renders a short answer question with text input
 */
export const ShortAnswerQuestion = ({ 
  question, 
  answer, 
  onAnswerChange,
  readonly = false,
  characterLimit = 500
}) => {
  return (
    <div className="question-container" style={{ marginBottom: '20px' }}>
      <div className="question-header" style={{ marginBottom: '12px' }}>
        <h4 style={{ margin: '0 0 4px 0', fontSize: '15px', fontWeight: '600', color: '#1f2937' }}>
          {question.question}
        </h4>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: '12px', color: '#6b7280', fontWeight: '500' }}>
            {question.points} point{question.points !== 1 ? 's' : ''}
          </span>
          <span style={{ fontSize: '11px', color: '#9ca3af' }}>
            {(answer || '').length} / {characterLimit}
          </span>
        </div>
      </div>
      
      <textarea
        value={answer || ''}
        onChange={(e) => onAnswerChange(question.id, e.target.value)}
        disabled={readonly}
        maxLength={characterLimit}
        placeholder="Type your answer here..."
        style={{
          width: '100%',
          minHeight: '100px',
          padding: '10px',
          borderRadius: '6px',
          border: '1px solid #d1d5db',
          fontSize: '14px',
          fontFamily: 'Arial, sans-serif',
          resize: 'vertical',
          opacity: readonly ? 0.6 : 1,
          cursor: readonly ? 'not-allowed' : 'text'
        }}
      />
    </div>
  );
};

/**
 * EssayQuestion Component
 * Renders an essay question with rich text area
 */
export const EssayQuestion = ({ 
  question, 
  answer, 
  onAnswerChange,
  readonly = false,
  characterLimit = 5000
}) => {
  return (
    <div className="question-container" style={{ marginBottom: '20px' }}>
      <div className="question-header" style={{ marginBottom: '12px' }}>
        <h4 style={{ margin: '0 0 4px 0', fontSize: '15px', fontWeight: '600', color: '#1f2937' }}>
          {question.question}
        </h4>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: '12px', color: '#6b7280', fontWeight: '500' }}>
            {question.points} point{question.points !== 1 ? 's' : ''} - Essay (requires manual grading)
          </span>
          <span style={{ fontSize: '11px', color: '#9ca3af' }}>
            {(answer || '').length} / {characterLimit}
          </span>
        </div>
      </div>
      
      <textarea
        value={answer || ''}
        onChange={(e) => onAnswerChange(question.id, e.target.value)}
        disabled={readonly}
        maxLength={characterLimit}
        placeholder="Write your essay here. Take your time to explain your thoughts clearly..."
        style={{
          width: '100%',
          minHeight: '200px',
          padding: '12px',
          borderRadius: '6px',
          border: '2px solid #e5e7eb',
          fontSize: '14px',
          fontFamily: 'Arial, sans-serif',
          resize: 'vertical',
          opacity: readonly ? 0.6 : 1,
          cursor: readonly ? 'not-allowed' : 'text'
        }}
      />
    </div>
  );
};

/**
 * MatchingQuestion Component
 * Renders a matching question where students pair items using radio buttons
 */
export const MatchingQuestion = ({ 
  question, 
  answer, 
  onAnswerChange,
  readonly = false 
}) => {
  const leftItems = question.options || [];
  const rightItems = question.items || [];

  // Parse answer from string if needed
  let answerObj = answer || {};
  if (typeof answer === 'string') {
    try {
      answerObj = JSON.parse(answer);
    } catch {
      answerObj = {};
    }
  }

  const renderSelected = (leftItem) => {
    const selected = answerObj[leftItem];
    return selected ? (
      <span style={{ color: '#1d4ed8', fontWeight: 600 }}>{selected}</span>
    ) : (
      <span style={{ color: '#6b7280' }}>No match selected</span>
    );
  };

  return (
    <div className="question-container" style={{ marginBottom: '20px' }}>
      <div className="question-header" style={{ marginBottom: '16px' }}>
        <h4 style={{ margin: '0 0 4px 0', fontSize: '15px', fontWeight: '600', color: '#1f2937' }}>
          {question.question}
        </h4>
        <span style={{ fontSize: '12px', color: '#6b7280', fontWeight: '500' }}>
          {question.points} point{question.points !== 1 ? 's' : ''}
        </span>
      </div>

      <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: '16px' }}>
        Match each statement on the left with the most appropriate answer on the right.
      </p>

      <div style={{ display: 'grid', gap: '14px' }}>
        {leftItems.map((leftItem, idx) => (
          <div
            key={`${leftItem}-${idx}`}
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr auto',
              gap: '12px',
              alignItems: 'center',
              padding: '16px',
              borderRadius: '12px',
              border: '1px solid #d1d5db',
              backgroundColor: '#ffffff'
            }}
          >
            <div>
              <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '6px' }}>
                {String.fromCharCode(65 + idx)}.
              </div>
              <div style={{ fontSize: '15px', color: '#111827', fontWeight: 600 }}>
                {leftItem}
              </div>
            </div>

            <div style={{ display: 'grid', gap: '8px', minWidth: 220 }}>
              <select
                value={answerObj[leftItem] || ''}
                onChange={(e) => onAnswerChange(question.id, { ...answerObj, [leftItem]: e.target.value })}
                disabled={readonly}
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: '8px',
                  border: '1px solid #cbd5e1',
                  backgroundColor: readonly ? '#f3f4f6' : '#ffffff',
                  color: '#111827',
                  cursor: readonly ? 'not-allowed' : 'pointer'
                }}
              >
                <option value="" disabled>
                  Select match
                </option>
                {rightItems.map((item, itemIdx) => (
                  <option key={`${item}-${itemIdx}`} value={item}>
                    {item}
                  </option>
                ))}
              </select>
              <div style={{ fontSize: '12px', color: '#6b7280' }}>
                Selected: {renderSelected(leftItem)}
              </div>
            </div>
          </div>
        ))}
      </div>

      {rightItems.length > 0 && (
        <div style={{ marginTop: '18px', padding: '14px 16px', borderRadius: '12px', backgroundColor: '#f8fafc', border: '1px solid #e2e8f0' }}>
          <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '10px' }}>Possible matches</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {rightItems.map((item, idx) => (
              <span key={`${item}-${idx}`} style={{ padding: '8px 12px', borderRadius: '999px', backgroundColor: '#eff6ff', color: '#1d4ed8', fontSize: '13px', fontWeight: 600 }}>
                {String.fromCharCode(65 + idx)}. {item}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * QuestionRenderer Component
 * Main component that renders any question type
 */
export const QuestionRenderer = ({ 
  question, 
  answer, 
  onAnswerChange,
  readonly = false 
}) => {
  const questionType = question.type || 'essay';
  
  switch (questionType) {
    case 'multipleChoice':
      return (
        <MultiChoiceQuestion 
          question={question}
          answer={answer}
          onAnswerChange={onAnswerChange}
          readonly={readonly}
        />
      );
    
    case 'shortAnswer':
      return (
        <ShortAnswerQuestion 
          question={question}
          answer={answer}
          onAnswerChange={onAnswerChange}
          readonly={readonly}
        />
      );
    
    case 'essay':
      return (
        <EssayQuestion 
          question={question}
          answer={answer}
          onAnswerChange={onAnswerChange}
          readonly={readonly}
        />
      );
    
    case 'matching':
      return (
        <MatchingQuestion 
          question={question}
          answer={answer}
          onAnswerChange={onAnswerChange}
          readonly={readonly}
        />
      );
    
    default:
      return (
        <div style={{ padding: '12px', backgroundColor: '#fef3c7', borderRadius: '6px', color: '#92400e' }}>
          Unknown question type: {questionType}
        </div>
      );
  }
};
